import React, { useState } from "react";
import { Check, Globe, MapPin, Map } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./styles.scss";
import Header from "../../component/header";
import SubHeader from "../homescreen/subHeader";
import { API } from "../../Config/API";
import { POST } from "../../utils/apiCalls";
import moment from "moment";
import { useSelector } from "react-redux";
import { Button, notification } from "antd";

interface Plan {
  name: string;
  oneYearPrice: string;
  oneYearNumericPrice: number;
  twoYearPrice: string;
  twoYearNumericPrice: number;
  currency: string;
  description: string;
  icon: any;
  features: string[];
  buttonText: string;
  highlight: boolean;
}

interface Benefit {
  icon: string;
  title: string;
  description: string;
}

const SubscriptionPlans = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState([]);
  const [planDuration, setPlanDuration] = useState<"1year" | "2year">("1year");
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>(
    {}
  );
  const date = searchParams.get("date");
  const edition = searchParams.get("edition");

  const user = useSelector((state: any) => state?.User?.user);

  const handleSubscribe = async (plan: Plan) => {
    if (!user || !user?.id) {
      console.log("User not logged in, navigating to /auth");
      navigate("/auth", {
        state: {
          redirectTo: "/SubscriptionPlans",
          planDetails: {
            planName: plan.name,
            price:
              planDuration === "1year" ? plan.oneYearPrice : plan.twoYearPrice,
            numericPrice:
              planDuration === "1year"
                ? plan.oneYearNumericPrice
                : plan.twoYearNumericPrice,
            currency: plan.currency,
            period: planDuration === "1year" ? "/year" : "/2 years",
            description: plan.description,
            features: plan.features,
            isTwoYear: planDuration === "2year",
          },
        },
      });
      return;
    }

    console.log("User logged in, proceeding with subscription for:", plan.name);
    setLoadingStates((prev) => ({ ...prev, [plan.name]: true }));

    try {
      const response: any = await POST(API.CHECK_USER_SUBSCRIPTION, {
        user: user?.id,
        edition: plan.name,
      });

      if (response?.status === 200) {
        console.log(
          "Subscription check successful, navigating to CheckoutPage"
        );
        navigate("/CheckoutPage", {
          state: {
            planName: plan.name,
            price:
              planDuration === "1year" ? plan.oneYearPrice : plan.twoYearPrice,
            numericPrice:
              planDuration === "1year"
                ? plan.oneYearNumericPrice
                : plan.twoYearNumericPrice,
            currency: plan.currency,
            period: planDuration === "1year" ? "/year" : "/2 years",
            description: plan.description,
            features: plan.features,
            isTwoYear: planDuration === "2year",
          },
        });
      } else if (response?.status === 250) {
        console.log("Active subscription found");
        notification.info({
          message: "Active Subscription",
          description:
            "You already have an active subscription for this edition.",
        });
      }
    } catch (error) {
      console.error("Subscription error:", error);
      notification.error({
        message: "Error",
        description: "Failed to process subscription. Please try again.",
      });
    } finally {
      setLoadingStates((prev) => ({ ...prev, [plan.name]: false }));
    }
  };

  const plans: Plan[] = [
    // {
    //   name: "International",
    //   oneYearPrice: "$ 29.00",
    //   oneYearNumericPrice: 29.0,
    //   twoYearPrice: "$ 49.00",
    //   twoYearNumericPrice: 49.0,
    //   currency: "$",
    //   description: "Access to our International E-paper edition",
    //   icon: Globe,
    //   features: [
    //     "International news coverage",
    //     "Global business insights",
    //     "World market analysis",
    //     "International sports coverage",
    //     "Multi-language support",
    //     "24/7 global news updates",
    //   ],
    //   buttonText: "Subscribe Now",
    //   highlight: false,
    // },
    // {
    //   name: "Qatar",
    //   oneYearPrice: "QR 190.00",
    //   oneYearNumericPrice: 190.0,
    //   twoYearPrice: "QR 290.00",
    //   twoYearNumericPrice: 290.0,
    //   currency: "QR",
    //   description: "Complete access to Qatar E-paper edition",
    //   icon: MapPin,
    //   features: [
    //     "Local Qatar news",
    //     "Regional business coverage",
    //     "Qatar community updates",
    //     "Local events calendar",
    //     "Arabic language support",
    //     "Premium regional content",
    //   ],
    //   buttonText: "Subscribe Now",
    //   highlight: true,
    // },
    {
      name: "India",
      oneYearPrice: "₹ 490.00",
      oneYearNumericPrice: 490.0,
      twoYearPrice: "₹ 690.00",
      twoYearNumericPrice: 690.0,
      currency: "₹",
      description: "Full access to Indian E-paper edition",
      icon: Map,
      features: [
        "Pan-India News & Analysis",
        "Bengaluru & Karnataka Focus",
        "Features, Wellness & Learning",
        "Bold Opinions & Editorials",
        "Business & World Affairs",
        "Complete Sports Coverage",
      ],
      buttonText: "Subscribe Now",
      highlight: false,
    },
  ];

  const benefits: Benefit[] = [
    {
      icon: "🌟",
      title: "Early Access",
      description: "Get the newspaper before it hits the stands",
    },
    {
      icon: "📱",
      title: "Multi-device Access",
      description: "Read on your phone, tablet, or computer",
    },
    {
      icon: "📚",
      title: "Archive Access",
      description: "Browse through past editions anytime",
    },
    {
      icon: "🎯",
      title: "Personalization",
      description: "Customize your reading experience",
    },
  ];

  const loadData = async (obj: any) => {
    try {
      const response: any = await POST(API.EPAPER, obj);
      setData(response?.length ? response : []);
    } catch (err) {
      console.error("Error loading data:", err);
    }
  };

  return (
    <div>
      <Header onChange={loadData} />
      <SubHeader
        edition={edition}
        date={date ? date : moment(new Date()).format("YYYY-MM-DD")}
      />

      <div className="subscription-plans min-vh-100 bg-light py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h4 className="text-uppercase small fw-bold subscription-title mb-3">
              NEWS TRIAL E-PAPER SUBSCRIPTION
            </h4>
            <h1 className="display-5 fw-bold mb-4">
              Select Your E-Paper Edition
            </h1>
            <div className="d-inline-flex bg-white rounded-pill p-1 mb-4">
              <button
                className={`btn rounded-pill px-4 ${
                  planDuration === "1year"
                    ? "btn-primary"
                    : "btn-outline-primary"
                }`}
                onClick={() => setPlanDuration("1year")}
              >
                1 Year Plan
              </button>
              <button
                className={`btn rounded-pill px-4 ms-2 ${
                  planDuration === "2year"
                    ? "btn-primary"
                    : "btn-outline-primary"
                }`}
                onClick={() => setPlanDuration("2year")}
              >
                2 Year Plan
              </button>
            </div>
          </div>

          <div className="row g-4 mb-5">
            {plans.map((plan) => {
              const IconComponent = plan.icon;
              const price =
                planDuration === "1year"
                  ? plan.oneYearPrice
                  : plan.twoYearPrice;
              const period = planDuration === "1year" ? "/year" : "/2 years";

              return (
                <div key={plan.name} className="col-md-4">
                  <div
                    className={`card plan-card h-100 ${
                      plan.highlight ? "highlight-card" : ""
                    }`}
                  >
                    <div className="card-body p-4">
                      <div
                        className={`plan-icon-wrapper ${
                          plan.highlight ? "highlight" : ""
                        }`}
                      >
                        <IconComponent size={24} />
                      </div>

                      <h3 className="card-title h4 mt-4">{plan.name}</h3>
                      <div className="pricing mt-3">
                        <span className="h2 fw-bold">{price}</span>
                        <span className="ms-1 opacity-75">{period}</span>
                      </div>
                      <p className="mt-3 opacity-75">{plan.description}</p>

                      <ul className="list-unstyled mt-4">
                        {plan.features.map((feature, index) => (
                          <li
                            key={index}
                            className="d-flex align-items-start mb-3"
                          >
                            <Check
                              size={20}
                              className="me-2 flex-shrink-0 mt-1 check-icon"
                            />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>

                      <Button
                        loading={loadingStates[plan.name] || false}
                        size="large"
                        className={`btn w-100 mt-4 ${
                          plan.highlight ? "btn-light" : "subscribe-btn"
                        }`}
                        onClick={() => handleSubscribe(plan)}
                        disabled={loadingStates[plan.name]}
                      >
                        {plan.buttonText}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="card benefits-section mb-5">
            <div className="card-body p-5">
              <h2 className="text-center mb-5">Subscription Benefits</h2>
              <div className="row g-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="col-md-3 text-center">
                    <div className="benefit-icon">{benefit.icon}</div>
                    <h3 className="h5 mb-2">{benefit.title}</h3>
                    <p className="text-muted small">{benefit.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPlans;
